/**
 * FieldZoneTriage — Mobile-first list of all open deviation zones across the
 * project, with single-tap ack/resolve and quick photo attachment.
 *
 * Backed by GET /scan3d/deviation-comparisons/_zones/triage and the per-zone
 * PATCH `/zones/{zoneId}/triage` endpoint. Designed for site supervisors on
 * phones/tablets — single column, large tap targets, no horizontal scroll.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Filter, AlertTriangle, AlertCircle, Info, CheckCircle2, Camera, MessageSquare, RefreshCw } from 'lucide-react';

const SEV = {
  CRITICAL: { color: '#ef4444', icon: AlertTriangle, label: 'Critical' },
  MAJOR:    { color: '#f97316', icon: AlertCircle,   label: 'Major' },
  MINOR:    { color: '#eab308', icon: Info,          label: 'Minor' },
};
const STATUSES = [
  { key: 'OPEN',     label: 'Open',     color: '#f59e0b' },
  { key: 'ACK',      label: 'Acked',    color: '#3b82f6' },
  { key: 'RESOLVED', label: 'Resolved', color: '#10b981' },
];

export default function FieldZoneTriage({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSev, setFilterSev] = useState('');
  const [filterStat, setFilterStat] = useState('OPEN');
  const [comment, setComment] = useState({});

  const refresh = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/_zones/triage`, {
        params: { ...(filterSev ? { severity: filterSev } : {}), limit: 200 },
      });
      setItems(r.data?.zones || []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [projectId, filterSev]);

  const filtered = useMemo(() => {
    if (!filterStat) return items;
    return items.filter(z => (z.status || 'OPEN') === filterStat);
  }, [items, filterStat]);

  const counts = useMemo(() => {
    const c = { OPEN: 0, ACK: 0, RESOLVED: 0 };
    for (const z of items) c[z.status || 'OPEN'] = (c[z.status || 'OPEN'] || 0) + 1;
    return c;
  }, [items]);

  const setStatus = async (zone, status) => {
    try {
      // We need the comparison's ObjectId here; the triage list returns code only.
      // Resolve via the comparisons list (cached server-side, very cheap).
      const cmpResp = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons?code=${zone.comparisonCode}`);
      const cmp = (cmpResp.data?.comparisons || []).find(c => c.comparisonCode === zone.comparisonCode);
      if (!cmp) { toast.error('Comparison not found'); return; }
      const payload = { status, ...(comment[zone.zoneId] ? { comment: comment[zone.zoneId] } : {}) };
      await api.patch(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${cmp.id}/zones/${zone.zoneId}/triage`, payload);
      toast.success(`${zone.zoneId} → ${status}`);
      setComment(c => ({ ...c, [zone.zoneId]: '' }));
      refresh();
    } catch (e) { toast.error(`Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const attachPhoto = async (zone, file) => {
    if (!file) return;
    try {
      const cmpResp = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons?code=${zone.comparisonCode}`);
      const cmp = (cmpResp.data?.comparisons || []).find(c => c.comparisonCode === zone.comparisonCode);
      if (!cmp) { toast.error('Comparison not found'); return; }
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${cmp.id}/zones/${zone.zoneId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Photo attached to ${zone.zoneId}`);
      refresh();
    } catch (e) { toast.error(`Photo failed: ${e?.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="space-y-3 max-w-2xl mx-auto" data-testid="field-zone-triage-tab">
      {/* Filter bar — sticky on mobile */}
      <div className="sticky top-0 z-10 rounded-xl p-2 space-y-2"
           style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <div className="flex items-center gap-1 text-[11px] flex-wrap">
          <Filter className="w-3 h-3 opacity-60" />
          <span className="opacity-70 mr-1">Severity</span>
          {['', 'CRITICAL', 'MAJOR', 'MINOR'].map(s => (
            <button key={s || 'ALL'} onClick={() => setFilterSev(s)}
              className="px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{
                background: filterSev === s ? (SEV[s]?.color || '#06b6d4') : '#0a0e14',
                color: filterSev === s ? '#000' : '#9ca3af',
                border: '1px solid #1f2937',
              }}
              data-testid={`triage-filter-sev-${s || 'all'}`}>
              {s || 'All'}
            </button>
          ))}
          <button onClick={refresh}
            className="ml-auto p-1 rounded text-[10px]"
            style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }}
            aria-label="Refresh"
            data-testid="triage-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-1 text-[11px] flex-wrap">
          <span className="opacity-70 mr-1">Status</span>
          {STATUSES.map(s => (
            <button key={s.key} onClick={() => setFilterStat(s.key)}
              className="px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{
                background: filterStat === s.key ? `${s.color}33` : '#0a0e14',
                color: filterStat === s.key ? s.color : '#9ca3af',
                border: `1px solid ${filterStat === s.key ? s.color : '#1f2937'}`,
              }}
              data-testid={`triage-filter-status-${s.key}`}>
              {s.label} <span className="opacity-60 ml-1">({counts[s.key] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {loading && filtered.length === 0 && (
        <div className="text-xs opacity-50 py-6 text-center">Loading…</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="text-xs opacity-50 py-6 text-center">No zones match this filter. 🎉</div>
      )}

      {/* Cards */}
      {filtered.map((z, idx) => {
        const sev = SEV[z.severity] || SEV.MINOR;
        const Icon = sev.icon;
        return (
          <div key={`${z.comparisonCode}-${z.zoneId}`} className="rounded-xl p-3 space-y-2"
               style={{ background: '#0f1722', border: `1px solid ${sev.color}33` }}
               data-testid={`triage-zone-card-${idx}`}>
            <div className="flex items-start gap-2">
              <div className="px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1"
                   style={{ background: `${sev.color}22`, color: sev.color }}>
                <Icon className="w-3 h-3" /> {sev.label.toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{z.zoneId}</div>
                <div className="text-[10px] opacity-60">
                  {z.comparisonCode} · {z.executedAt ? new Date(z.executedAt).toLocaleString() : '—'}
                </div>
              </div>
              {z.raidItemId && (
                <a href={`/raid?projectId=${projectId}&itemId=${encodeURIComponent(z.raidItemId)}`}
                   target="_blank" rel="noreferrer"
                   className="px-1.5 py-0.5 rounded text-[10px]"
                   style={{ background: '#ef444433', color: '#ef4444' }}
                   data-testid={`triage-raid-${z.zoneId}`}>
                  {z.raidItemId}
                </a>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
              <div className="rounded p-1.5" style={{ background: '#0a0e14' }}>
                <div className="opacity-60">Max</div>
                <div className="text-sm font-mono">{(z.maxDeviationMm || 0).toFixed(1)}<span className="text-[9px]">mm</span></div>
              </div>
              <div className="rounded p-1.5" style={{ background: '#0a0e14' }}>
                <div className="opacity-60">Mean</div>
                <div className="text-sm font-mono">{(z.meanDeviationMm || 0).toFixed(1)}<span className="text-[9px]">mm</span></div>
              </div>
              <div className="rounded p-1.5" style={{ background: '#0a0e14' }}>
                <div className="opacity-60">Points</div>
                <div className="text-sm font-mono">{(z.pointCount || 0).toLocaleString()}</div>
              </div>
            </div>

            {z.photos?.length > 0 && (
              <div className="flex gap-1 overflow-x-auto pb-1">
                {z.photos.slice(0, 5).map((p, i) => (
                  <img key={i} src={p.url || p} alt={`evidence ${i}`}
                       className="w-16 h-16 rounded object-cover flex-shrink-0"
                       style={{ border: '1px solid #1f2937' }} />
                ))}
                {z.photos.length > 5 && (
                  <div className="w-16 h-16 rounded flex items-center justify-center text-[10px] opacity-60 flex-shrink-0"
                       style={{ background: '#0a0e14', border: '1px solid #1f2937' }}>
                    +{z.photos.length - 5}
                  </div>
                )}
              </div>
            )}

            <textarea
              value={comment[z.zoneId] || ''}
              onChange={(e) => setComment(c => ({ ...c, [z.zoneId]: e.target.value }))}
              placeholder="Field note (optional)…"
              rows={2}
              className="w-full px-2 py-1 rounded text-xs resize-none"
              style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
              data-testid={`triage-comment-${z.zoneId}`} />

            <div className="flex items-center gap-1.5">
              <label className="flex-1 cursor-pointer">
                <input type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={(e) => attachPhoto(z, e.target.files?.[0])}
                       data-testid={`triage-photo-${z.zoneId}`} />
                <div className="flex items-center justify-center gap-1 py-1.5 rounded text-[11px] font-semibold"
                     style={{ background: '#0a0e14', color: '#06b6d4', border: '1px dashed #06b6d466' }}>
                  <Camera className="w-3.5 h-3.5" /> Photo
                </div>
              </label>
              {z.status !== 'ACK' && z.status !== 'RESOLVED' && (
                <button onClick={() => setStatus(z, 'ACK')}
                        className="flex-1 py-1.5 rounded text-[11px] font-semibold"
                        style={{ background: '#3b82f6', color: '#fff' }}
                        data-testid={`triage-ack-${z.zoneId}`}>
                  <MessageSquare className="w-3.5 h-3.5 inline mr-0.5" /> Ack
                </button>
              )}
              {z.status !== 'RESOLVED' && (
                <button onClick={() => setStatus(z, 'RESOLVED')}
                        className="flex-1 py-1.5 rounded text-[11px] font-semibold"
                        style={{ background: '#10b981', color: '#000' }}
                        data-testid={`triage-resolve-${z.zoneId}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-0.5" /> Resolve
                </button>
              )}
              {z.status === 'RESOLVED' && (
                <button onClick={() => setStatus(z, 'OPEN')}
                        className="flex-1 py-1.5 rounded text-[11px] font-semibold opacity-70"
                        style={{ background: '#0a0e14', color: '#f59e0b', border: '1px solid #1f2937' }}
                        data-testid={`triage-reopen-${z.zoneId}`}>
                  Re-open
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
